import { useEffect, useState } from 'react';
import {
  listArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  uploadAdminImage,
} from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';
import { cx } from '../../components/shared/ui/utils';

const emptyForm = {
  id: null,
  title: '',
  slug: '',
  category: '',
  readTime: '',
  imageUrl: '',
  excerpt: '',
  content: '',
  publishedAt: '',
  isActive: true,
};

const AdminPackageArticles = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await listArticles();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load articles');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectItem = (item) => {
    setForm({
      id: item.id,
      title: item.title || '',
      slug: item.slug || '',
      category: item.category || '',
      readTime: item.read_time || '',
      imageUrl: item.image_url || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      publishedAt: item.published_at || '',
      isActive: item.is_active !== false,
    });
  };

  const handleSave = async () => {
    setError('');
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        category: form.category,
        read_time: form.readTime,
        image_url: form.imageUrl,
        excerpt: form.excerpt,
        content: form.content,
        published_at: form.publishedAt || undefined,
        is_active: form.isActive,
      };
      if (form.id) await updateArticle(form.id, payload);
      else await createArticle(payload);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!window.confirm('Delete this article?')) return;
    try {
      await deleteArticle(form.id);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, imageUrl: res.path }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
          <h1 className="text-xl font-semibold text-slate-900">Articles</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setForm(emptyForm)}>
            New article
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectItem(item)}
              className={cx(
                'flex w-full flex-col rounded-xl px-3 py-2 text-left text-sm',
                form.id === item.id ? 'bg-slate-100' : 'hover:bg-slate-50'
              )}
            >
              <span className="font-semibold text-slate-900">{item.title}</span>
              <span className="text-xs text-slate-500">{item.category}</span>
            </button>
          ))}
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 space-y-3">
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Slug"
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
          />
          <div className="grid gap-2 md:grid-cols-2">
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Category"
              value={form.category}
              onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
            />
            <input
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Read time"
              value={form.readTime}
              onChange={(e) => setForm((prev) => ({ ...prev, readTime: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-3 text-sm">
            <input type="file" onChange={handleUpload} />
            {form.imageUrl && <span className="text-xs text-slate-500">Image selected</span>}
          </div>
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={3}
            placeholder="Excerpt"
            value={form.excerpt}
            onChange={(e) => setForm((prev) => ({ ...prev, excerpt: e.target.value }))}
          />
          <textarea
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            rows={5}
            placeholder="Content"
            value={form.content}
            onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
          />
          <input
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
            placeholder="Published date (YYYY-MM-DD)"
            value={form.publishedAt}
            onChange={(e) => setForm((prev) => ({ ...prev, publishedAt: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button variant="outline" onClick={handleDelete}>
                Delete
              </Button>
            )}
            <Button onClick={handleSave}>Save article</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPackageArticles;
